import { useState } from 'react';
import { Box, Button, Typography, Modal } from '@mui/material'; // Material-UI components
import { useUpdatePropertyMutation } from '../../store/docApi';
import ColorChartThumbnail from './ColorChartThumbnail';

export function DoubleKnitChart({ data1, data2, project, depth }) {
  const [rows] = useState(data1 ? data1.length : 0);
  const [cols] = useState(data1 && data1[0] ? data1[0].length : 0);
  const [currentRow, setCurrentRow] = useState(getCurrentRow());
  const [CELL_SIZE] = useState(24);
  const [modalOpen, setModalOpen] = useState(false);

  const [updateProperty, result] = useUpdatePropertyMutation();

  async function updateCurrentRow(num) {
    setCurrentRow(num);
    if (project) {
      await updateProperty({
        label: 'project',
        pk: project.pk,
        id: project.id,
        name: `progress/${depth}_row`,
        value: num
      });
    }
  }

  function getCurrentRow() {
    return project && project.progress && project.progress[`${depth}_row`] ? project.progress[`${depth}_row`] : -1;
  }

  const dynamicStyles = {
    cell: {
      height: CELL_SIZE,
      width: CELL_SIZE,
      borderWidth: 0.5,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },
    cellnum: {
      height: CELL_SIZE,
      width: CELL_SIZE * 2,
      fontSize: CELL_SIZE / 3,
      textAlign: 'center',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  };

  function getCellColor(num, front) {
    return num ? (front ? 'gainsboro' : 'lightgrey') : 'white';
  }

  return data1 && data2 ? (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Double Knit Chart
      </Typography>
      {result && result.isError && <Typography color="error">Error saving</Typography>}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2 }}>
        <ColorChartThumbnail data={data1} />
        <ColorChartThumbnail data={data2} />
      </Box>
      <Box sx={styles.chartContainer}>
        <Box sx={{ overflowX: 'auto', display: 'flex' }}>
          <Box sx={styles.horizontal}>
            <Box sx={styles.chart}>
              {data1.map((step, i) => (
                <Box key={i} sx={styles.step}>
                  {step.map((col, j) => (
                    <Box key={j} sx={{ display: 'flex', flexDirection: 'row' }} onClick={() => setCurrentRow(i)}>
                      <Box
                        sx={{
                          ...dynamicStyles.cell,
                          backgroundColor: getCellColor(col, true)
                        }}
                      >
                        <Typography>o</Typography>
                      </Box>
                      <Box
                        sx={{
                          ...dynamicStyles.cell,
                          backgroundColor: getCellColor(data2[i][j], false)
                        }}
                      />
                    </Box>
                  ))}
                  <Typography sx={dynamicStyles.cellnum}>{data1.length - i}</Typography>
                </Box>
              ))}
              <Box sx={styles.step}>
                {data1[0] &&
                  data1[0].map((col, j) => (
                    <Typography key={j} sx={dynamicStyles.cellnum}>
                      {data1[0].length - j}
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Box>
          {cols && rows && (
            <Box sx={styles.decor}>
              {Array.from({ length: rows }).map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    position: 'absolute',
                    top: (rows - i - 1) * CELL_SIZE,
                    height: CELL_SIZE,
                    width: cols * CELL_SIZE * 2,
                    cursor: 'pointer'
                  }}
                  onClick={() => (currentRow === i ? updateCurrentRow(-1) : updateCurrentRow(i))}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Button onClick={() => updateCurrentRow(currentRow + 1)} sx={styles.nextButton}>
        Next Row
      </Button>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            boxShadow: 24,
            backgroundColor: 'background.paper'
          }}
        >
          <Typography variant="h6">Modal Content</Typography>
          <Button onClick={() => setModalOpen(false)}>Close</Button>
        </Box>
      </Modal>
    </Box>
  ) : (
    <Typography>No data available</Typography>
  );
}

const styles = {
  horizontal: {
    display: 'flex',
    flexDirection: 'row'
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    marginLeft: 12
  },
  decor: {
    position: 'absolute'
  },
  rowHighlight: {
    borderColor: 'yellow',
    borderStyle: 'solid',
    borderWidth: 3
  },
  chart: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap'
  },
  step: {
    display: 'flex',
    flexDirection: 'row',
    alignSelf: 'flex-start'
  },
  nextButton: {
    marginTop: 16
  }
};
